if (!customElements.get('flsk-engraving-section')) {
  customElements.define(
    'flsk-engraving-section',
    class FlskEngravingSection extends HTMLElement {
      // How long a typed (but not yet purchased) engraving survives a reload / variant swap.
      static STORAGE_TTL_MS = 30 * 60 * 1000;

      constructor() {
        super();
        this.engravingCanvas = this.querySelector('#engravingCanvasText');
        this.engravingCtx = this.engravingCanvas.getContext('2d');
        this.engravingCanvasBackground = this.querySelector('#engravingCanvasBackground');
        this.engravingBackgroundCtx = this.engravingCanvasBackground.getContext('2d');

        this.engravingCanvas.width = 480;
        this.engravingCanvas.height = 480;
        this.engravingCanvasBackground.width = 480;
        this.engravingCanvasBackground.height = 480;

        const engravingBackgroundCtx = this.engravingBackgroundCtx;
        const background = new Image();
        background.src = this.dataset.engravingImage;
        background.onload = function() {
          engravingBackgroundCtx.drawImage(background, 0, 0);
        };

        this.allowedCharacters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

        this.storageKey = 'engravingText' + this.dataset.product;
        this.storageKeyFont = 'engravingFont' + this.dataset.product; // legacy key, only cleaned up

        this.engravingTextField = this.querySelector('#engravingTextField');
        this.hiddenInputFont = this.querySelector('.hidden-input-font');
        this.hiddenInputIds = this.querySelectorAll('.hidden-input-id');
        this.hiddenInputFonts = this.querySelectorAll('.hidden-input-font');
        this.hiddenInputTexts = this.querySelectorAll('.hidden-input-text');
        this.hiddenEngravingInputs = this.querySelectorAll('.engraving-hidden-inputs');
        this.engravingQty = this.querySelector('.hidden-engraving-qty');

        // The product form the hidden inputs belong to (form="..." attribute) and ITS quantity
        // input. Never `document.querySelector('.quantity__input')` – with items in the cart that
        // resolves to the cart drawer's first row, so the fee quantity never followed the product.
        this.formId = this.hiddenEngravingInputs.length ? this.hiddenEngravingInputs[0].getAttribute('form') : null;
        this.productForm = this.formId ? document.getElementById(this.formId) : null;
        this.qtyButton =
          (this.formId && document.querySelector(`input[name="quantity"][form="${this.formId}"]`)) ||
          (this.productForm && this.productForm.querySelector('input[name="quantity"]')) ||
          null;

        this.fontInputs = this.querySelectorAll('input.font-radio');
        this.maxInputLength = this.querySelector('#maxInputLength');
        this.fontName = this.querySelector('#fontName');
        this.currentInputLength = this.querySelector('#currentInputLength');

        this.engravingSubmitButton = this.querySelector('.submitEngraving');
        this.cancelEngravingButton = this.querySelector('.cancelEngraving');

        this.engravingValidationInfo = this.querySelector('#engravingValidationInfo');

        this.addEngravingId();

        if (this.qtyButton) {
          this.qtyButton.addEventListener('change', this.updateEngravingQty.bind(this));
          this.qtyButton.addEventListener('input', this.updateEngravingQty.bind(this));
        }

        // `input` fires for typing, paste, dictation, autocorrect and swipe keyboards; `keyup`
        // only for physical key presses – which is why pasted/autocorrected text used to be lost.
        this.engravingTextField.addEventListener('input', this.engravingInputValidation.bind(this));
        this.engravingTextField.addEventListener('input', this.writeInputEngraving.bind(this));
        this.engravingTextField.addEventListener('input', this.engravingDraw.bind(this));
        this.engravingTextField.addEventListener('input', this.updateEngravingTextStorage.bind(this));

        for (const fontInput of this.fontInputs) {
          fontInput.addEventListener('change', this.fontSwitch.bind(this, fontInput));
          fontInput.addEventListener('change', this.updateEngravingTextStorage.bind(this));
        }

        this.engravingSubmitButton.addEventListener('click', this.engravingDraw.bind(this));
        this.engravingSubmitButton.addEventListener('click', this.submitEngraving.bind(this));
        this.cancelEngravingButton.addEventListener('click', this.cancelEngraving.bind(this));
        this.cancelEngravingButton.addEventListener('click', this.removeEngravingTextStorage.bind(this));

        // Last line of defence: right before the product form is submitted, copy the VISIBLE
        // state (text, font, product quantity) into the hidden inputs. Whatever is on screen is
        // what gets ordered.
        if (this.productForm) {
          this.snapshotBeforeSubmitHandler = this.snapshotBeforeSubmit.bind(this);
          // Capture-phase on the form AND on its submit button: the button click fires before the
          // submit event, so the snapshot is taken before product-form.js builds its FormData in
          // every browser, regardless of at-target listener ordering.
          this.productForm.addEventListener('submit', this.snapshotBeforeSubmitHandler, true);
          this.productSubmitButton = this.productForm.querySelector('[type="submit"], button[name="add"]');
          if (this.productSubmitButton) {
            this.productSubmitButton.addEventListener('click', this.snapshotBeforeSubmitHandler, true);
          }
        }
      }

      isArmed() {
        return this.hiddenEngravingInputs.length > 0 && !this.hiddenEngravingInputs[0].disabled;
      }

      checkedFont() {
        const checked = this.querySelector('input.font-radio:checked');
        return checked ? checked.value : this.hiddenInputFont ? this.hiddenInputFont.value : '';
      }

      snapshotBeforeSubmit() {
        if (!this.isArmed()) return;
        const text = this.engravingTextField.value;
        if (!text || text.trim().length === 0) {
          // Armed but empty – never send an empty engraving (and no fee for it).
          this.cancelEngraving();
          this.removeEngravingTextStorage();
          return;
        }
        for (const hiddenInput of this.hiddenInputTexts) hiddenInput.value = text;
        const font = this.checkedFont();
        if (font) for (const hiddenInput of this.hiddenInputFonts) hiddenInput.value = font;
        this.updateEngravingQty();
        // Both hidden id inputs (product + fee line) must carry the same id.
        const id = this.hiddenInputIds.length ? this.hiddenInputIds[0].value : '';
        for (const hiddenInput of this.hiddenInputIds) hiddenInput.value = id;
      }

      updateEngravingTextStorage() {
        try {
          localStorage.setItem(
            this.storageKey,
            JSON.stringify({ text: this.engravingTextField.value, font: this.checkedFont(), ts: Date.now() })
          );
        } catch (e) {
          /* storage unavailable – engraving still works for this page */
        }
      }

      removeEngravingTextStorage() {
        try {
          localStorage.removeItem(this.storageKey);
          localStorage.removeItem(this.storageKeyFont);
        } catch (e) {
          /* ignore */
        }
      }

      // Returns {text, font} when a non-expired engraving is stored for this product, else null.
      // Legacy plain-string values (pre-TTL) are discarded on purpose.
      readEngravingTextStorage() {
        let raw = null;
        try {
          raw = localStorage.getItem(this.storageKey);
        } catch (e) {
          return null;
        }
        if (!raw) return null;
        let parsed = null;
        try {
          parsed = JSON.parse(raw);
        } catch (e) {
          this.removeEngravingTextStorage();
          return null;
        }
        if (!parsed || typeof parsed !== 'object' || typeof parsed.text !== 'string' || !parsed.text) {
          this.removeEngravingTextStorage();
          return null;
        }
        if (!parsed.ts || Date.now() - parsed.ts > FlskEngravingSection.STORAGE_TTL_MS) {
          this.removeEngravingTextStorage();
          return null;
        }
        return parsed;
      }

      setEngravingTextStatus() {
        const stored = this.readEngravingTextStorage();
        if (!stored) return;
        this.engravingTextField.value = stored.text;
        this.writeInputEngraving();
        this.submitEngraving();
        this.engravingDraw();
      }

      setEngravingFont() {
        const stored = this.readEngravingTextStorage();
        if (!stored || !stored.font) return;
        const radio = this.querySelector('input.font-radio[value="' + stored.font + '"]');
        if (!radio) return;
        radio.checked = true;
        this.fontSwitch(radio);
      }

      connectedCallback() {
        this.engravingButton = document.querySelector('#EngravingProductButton');
        this.engravingButtonPreview = document.querySelector('#engravingButtonPreview');
        this.setEngravingFont();
        this.setEngravingTextStatus();

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (cartState) => {
          // cartData is the /cart/add response for product-form adds (single item or {items}),
          // but the whole cart object for cart-items updates – guard every shape.
          const data = cartState && cartState.cartData;
          if (!data) return;
          const ids = [];
          if (data.properties && data.properties['_Gravur ID']) ids.push(data.properties['_Gravur ID']);
          if (Array.isArray(data.items)) {
            data.items.forEach((item) => {
              if (item && item.properties && item.properties['_Gravur ID']) ids.push(item.properties['_Gravur ID']);
            });
          }
          const ownId = this.hiddenInputIds.length ? this.hiddenInputIds[0].value : null;
          if (ownId && ids.includes(ownId) && cartState.source === 'product-form') {
            this.cancelEngraving();
            this.removeEngravingTextStorage();
          }
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.productForm && this.snapshotBeforeSubmitHandler) {
          this.productForm.removeEventListener('submit', this.snapshotBeforeSubmitHandler, true);
          if (this.productSubmitButton) {
            this.productSubmitButton.removeEventListener('click', this.snapshotBeforeSubmitHandler, true);
          }
        }
      }

      engravingDraw() {
        const text = this.engravingTextField.value;
        this.engravingCtx.clearRect(0, 0, this.engravingCanvas.width, this.engravingCanvas.height);
        this.engravingCtx.fillStyle = this.dataset.engravingColor;
        this.engravingCtx.font = this.dataset.engravingSize + 'px \'' + this.hiddenInputFont.value + '\'';
        this.engravingCtx.textAlign = 'center';
        this.engravingCtx.fillText(text, parseInt(this.dataset.engravingX), parseInt(this.dataset.engravingY));
      }

      addEngravingId() {
        const allowedCharacters = this.allowedCharacters;

        function generateString(length) {
          let result = '';
          const charactersLength = allowedCharacters.length;
          for (let i = 0; i < length; i++) {
            result += allowedCharacters.charAt(Math.floor(Math.random() * charactersLength));
          }
          return result;
        }

        const hiddenEngravingId = this.hiddenInputIds;
        const randomId = generateString(16);
        for (const hiddenInput of hiddenEngravingId) {
          hiddenInput.value = randomId;
        }
      }

      updateEngravingQty() {
        if (!this.engravingQty) return;
        const qty = this.qtyButton ? parseInt(this.qtyButton.value, 10) : NaN;
        this.engravingQty.value = Number.isFinite(qty) && qty > 0 ? qty : 1;
      }

      engravingInputValidation() {
        this.engravingValidationInfo.style.display = 'none';
        if (this.engravingTextField.value.match(/[^a-zA-Z0-9!\/()=?:;.\-@ßéáíóúàèìòùäöüÁÉÍÓÚÀÈÌÒÙÄÖÜ\s]/g, '')) {
          this.engravingValidationInfo.style.display = '';
        }
        this.engravingTextField.value = this.engravingTextField.value.replace(/[^a-zA-Z0-9!\/()=?:;.\-@ßéáíóúàèìòùäöüÁÉÍÓÚÀÈÌÒÙÄÖÜ\s]/g, '');
      }

      fontSwitch(el) {
        if (el.checked) {
          this.fontName.innerHTML = el.value;
          this.engravingTextField.maxLength = el.dataset.fontLimitation;
          this.maxInputLength.innerHTML = el.dataset.fontLimitation;
          this.dataset.engravingSize = el.dataset.fontSize;
          for (const hiddenInput of this.hiddenInputFonts) {
            hiddenInput.value = el.value;
          }
          if (this.engravingTextField.value.length > this.engravingTextField.maxLength) {
            this.engravingTextField.value = this.engravingTextField.value.slice(0, this.engravingTextField.maxLength);
          }
          this.currentInputLength.innerHTML = this.engravingTextField.value.length.toString();
          this.engravingDraw();
          this.addEngravingId();
        }
      }

      cancelEngraving() {
        this.currentInputLength.innerHTML = '0';
        for (const hiddenInput of this.hiddenEngravingInputs) {
          hiddenInput.setAttribute('disabled', '');
        }
        this.engravingTextField.value = '';
        for (const hiddenInput of this.hiddenInputTexts) hiddenInput.value = '';
        this.engravingSubmitButton.setAttribute('disabled', '');
        if (this.engravingButton) this.engravingButton.classList.remove('active');
        if (this.engravingButtonPreview) this.engravingButtonPreview.textContent = '';
        this.engravingDraw();
      }

      submitEngraving() {
        if (!this.engravingTextField.value || this.engravingTextField.value.trim().length === 0) return;
        for (const hiddenInput of this.hiddenEngravingInputs) {
          hiddenInput.removeAttribute('disabled');
        }
        this.updateEngravingQty();
        if (this.engravingButton) this.engravingButton.classList.add('active');
      }

      writeInputEngraving() {
        this.currentInputLength.innerHTML = this.engravingTextField.value.length.toString();

        if (this.engravingTextField.value.length > 0) {
          this.engravingSubmitButton.removeAttribute('disabled');
        } else {
          this.engravingSubmitButton.setAttribute('disabled', '');
        }
        for (const hiddenInput of this.hiddenInputTexts) {
          hiddenInput.value = this.engravingTextField.value;
        }
        if (this.engravingButtonPreview) this.engravingButtonPreview.textContent = this.engravingTextField.value;
        this.addEngravingId();
      }
    }
  );
}

if (!customElements.get('flsk-modal-dialog')) {
  customElements.define(
    'flsk-modal-dialog',
    class FlskModalDialog extends ModalDialog {
      constructor() {
        super();
        for (const closeButton of this.querySelectorAll('[id^="ModalClose-"]')) {
          closeButton.addEventListener('click', this.hide.bind(this, false));
        }
        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
        if (this.classList.contains('media-modal')) {
          this.addEventListener('pointerup', (event) => {
            if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
          });
        } else {
          this.addEventListener('click', (event) => {
            if (event.target === this) this.hide();
          });
        }
      }
      connectedCallback() {
      }
    }
  );
}
